export const API_BASE = process.env.REACT_APP_API_URL || 'https://imole.to';

// WS_BASE must be origin-only (scheme + host). Each feed appends its own
// path via FEEDS[*].wsPath. We had a production .env that accidentally
// stored a full path-bearing URL here, producing concatenated WS paths
// like wss://imole.to/ws/strategies/p1s1/decisions//ws/p1s2/decisions/
// → "No route found" 1000s of times in the Django logs. Strip any path
// from whatever the env supplies so the bug can't recur.
export const WS_BASE = (() => {
  const raw = process.env.REACT_APP_WS_URL || API_BASE.replace(/^http/, 'ws');
  try {
    const u = new URL(raw);
    return `${u.protocol}//${u.host}`;        // origin only — no path/search/hash
  } catch {
    return raw.replace(/^http/, 'ws');         // fallback: best-effort scheme fix
  }
})();

export const FEEDS = {
  // P1S1 feed removed 2026-06-11 (full backend wipe).
  // GEMINI removed 2026-06-24; FAVOLA removed 2026-06-29 (Option A full wipe).
  STAGE0: {
    id:             'STAGE0',
    label:          'Stage 0',
    sublabel:       'S0-A · S0-B Observe',
    color:          '#9c6ef5',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/stage0_ab/decisions/',
    wsPath:         '/ws/stage0_ab/decisions/',
    extraRestPaths: [],
  },
  NQPBM: {
    id:             'NQPBM',
    label:          'NQ-PB-M',
    sublabel:       'Path B Morning · LONG · v1.2c',
    color:          '#10b981',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/nqpbm/decisions/',
    wsPath:         '/ws/strategies/nqpbm/decisions/',
    extraRestPaths: [],
  },
  NQPBA: {
    id:             'NQPBA',
    label:          'NQ-PB-A',
    sublabel:       'Path B Afternoon · LONG · v1.3',
    color:          '#0d9488',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/nqpba/decisions/',
    wsPath:         '/ws/strategies/nqpba/decisions/',
    extraRestPaths: [],
  },
  CAV1: {
    id:             'CAV1',
    label:          'CAV1',
    sublabel:       'Config A V1 · NQ Hybrid',
    color:          '#eab308',
    direction:      null,    // mixed: vgb_bull_flip → LONG, aa_short → SHORT
    restPath:       '/strategies/api/strategies/cav1/decisions/',
    wsPath:         '/ws/strategies/cav1/decisions/',
    extraRestPaths: [],
  },
  ABM: {
    id:             'ABM',
    label:          'ABAMETTA',
    sublabel:       '3 systems · Grade A/BP · NQ',
    color:          '#0ea5e9',                 // sky-500 — distinct from rest
    direction:      null,                       // ABML1=LONG, ABMS1/S2=SHORT
    restPath:       '/strategies/api/strategies/abm/decisions/',
    wsPath:         '/ws/strategies/abm/decisions/',
    extraRestPaths: [],
  },
  IMPERIAL: {
    id:             'IMPERIAL',
    label:          'Imperial',
    sublabel:       'NQ 30m ORB Swing · v3.1',
    color:          '#f43f5e',                 // rose-500 — distinct from existing palette
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/imperial/decisions/',
    wsPath:         '/ws/strategies/imperial/decisions/',
    extraRestPaths: [],
  },
  // P1S1_LEVELS feed removed 2026-06-11 (full backend wipe).
  TIDE: {
    id:             'TIDE',
    label:          'TIDE',
    sublabel:       'VGB20 · 1-min→5-min · NQ · v3',
    color:          '#f97316',                 // orange-500 — distinct from existing palette
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/tide/decisions/',
    wsPath:         '/ws/strategies/tide/decisions/',
    extraRestPaths: [],
  },
  // ANDE_LONGS / ANDE_SHORTS removed 2026-06-15 (full wipe).
  ASIKO_IGBEGA_V2: {
    id:             'ASIKO_IGBEGA_V2',
    label:          'Asiko Igbega v2',
    sublabel:       'v4-OPTIMAL · dual-hibernation · 4 sub-systems · NQ',
    color:          '#a855f7',                 // purple-500
    direction:      null,                      // mixed LONG + SHORT
    restPath:       '/strategies/api/strategies/asiko-igbega-v2/decisions/',
    wsPath:         '/ws/strategies/asiko-igbega-v2/decisions/',
    extraRestPaths: [],
  },
  MANSA_AA4_LONGS_NLA: {
    id:             'MANSA_AA4_LONGS_NLA',
    label:          'Mansa AA4 Longs',
    sublabel:       'NLA-locked · 3-gate · LONG · NQ',
    color:          '#f59e0b',                 // amber-500 — Mansa project tag
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/mansa-aa4-longs-nla/decisions/',
    wsPath:         '/ws/strategies/mansa-aa4-longs-nla/decisions/',
    extraRestPaths: [],
  },
  MANSA_AA4_SHORTS_NLA: {
    id:             'MANSA_AA4_SHORTS_NLA',
    label:          'Mansa AA4 Shorts',
    sublabel:       'NLA-locked · 3-gate · SHORT · NQ',
    color:          '#ef4444',                 // red-500 — short direction
    direction:      'SHORT',
    restPath:       '/strategies/api/strategies/mansa-aa4-shorts-nla/decisions/',
    wsPath:         '/ws/strategies/mansa-aa4-shorts-nla/decisions/',
    extraRestPaths: [],
  },
  DIMANCHE_V2: {
    id:             'DIMANCHE_V2',
    label:          'Dimanche v2',
    sublabel:       'Asiko pattern · 10 books · NQ',
    color:          '#06b6d4',                 // cyan-500 — distinct from other strategies
    direction:      null,                      // 4 Lux LONG + 3 AA4L LONG + 3 AAS SHORT
    restPath:       '/strategies/api/strategies/dimanche/decisions/',
    wsPath:         '/ws/strategies/dimanche/decisions/',
    extraRestPaths: [],
  },
  DOMINGO_SHORTS: {
    id:             'DOMINGO_SHORTS',
    label:          'Domingo Shorts',
    sublabel:       'v3-LOCKED · 6-gate · SHORT · NQ 15m',
    color:          '#f97316',                 // orange-500 — Domingo portfolio
    direction:      'SHORT',
    restPath:       '/strategies/api/strategies/domingo-shorts/decisions/',
    wsPath:         '/ws/strategies/domingo-shorts/decisions/',
    extraRestPaths: [],
  },
  DOMINGO_LONGS: {
    id:             'DOMINGO_LONGS',
    label:          'Domingo Longs',
    sublabel:       'v2-LOCKED · 3-gate · LONG · NQ 15m',
    color:          '#facc15',                 // yellow-400 — Domingo portfolio
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/domingo-longs/decisions/',
    wsPath:         '/ws/strategies/domingo-longs/decisions/',
    extraRestPaths: [],
  },
  // GBEMI feed removed 2026-06-10 (full backend wipe).
  PADA: {
    id:             'PADA',
    label:          'Padà',
    sublabel:       'v1-LOCKED · pure-price mean-rev · LONG · NQ 3m',
    color:          '#f472b6',                 // pink-400 — Padà portfolio
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/pada/decisions/',
    wsPath:         '/ws/strategies/pada/decisions/',
    extraRestPaths: [],
  },
  // STRATA feed removed 2026-06-11 (full backend wipe).
  // EMEKA removed 2026-06-24 (strategy retired, operator request).
  // NCHE removed 2026-06-24 (strategy retired, operator request).
  // FAVOLA removed 2026-06-29 (Option A full wipe).
  WURA: {
    id:             'WURA',
    label:          'Wura',
    sublabel:       'NQ 3m pre-market · MW+Lux+AA confluence · AA-Lull 04:30–06:30 + Deep-Europe 06:30–08:00 ET · −50bp stop / 09:30 ET time exit · fires only on OṢUPA SKIP · cert WURA-SKIP-V3-LOCK-20260620-92858500',
    color:          '#ca8a04',                 // gold-600 — "wura" (Yoruba: gold)
    direction:      null,                      // both directions
    restPath:       '/strategies/api/strategies/wura/decisions/',
    wsPath:         '/ws/strategies/wura/decisions/',
    extraRestPaths: [],
  },
  IYIPADA: {
    id:             'IYIPADA',
    label:          'Iyipada',
    sublabel:       'Cobra-15M Fade · NQ · tier ONEWAY/TAKE · arm+trail/cap + hard stop · cert ONEWAY 7D3EEB85 / TAKE 20DBDA78 · v2.0 feed-corrected · 81t · 72.8% WR · +9.0%',
    color:          '#e11d48',                 // rose-600 — "iyipada" (Yoruba: change/reversal)
    direction:      null,                      // both directions
    restPath:       '/strategies/api/strategies/iyipada/decisions/',
    wsPath:         '/ws/strategies/iyipada/decisions/',
    extraRestPaths: [],
  },
  OSUPA: {
    id:             'OSUPA',
    label:          'Osupa',
    sublabel:       'Overnight LONG V10 · NQ · 16:00 ET eval (close>sma100 + rising) · grade A/B take, C skip · regime gate (B skipped when pct_above>800bp) · scale-out 50/50 · cert MANSA-OSUPA-LONG-V10-1FB056C1 · 284t · +120.4% · 70% WR · −1.58% DD · Phase 1',
    color:          '#6366f1',                 // indigo-500 — "osupa" (Yoruba: moon → overnight)
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/osupa/decisions/',
    wsPath:         '/ws/strategies/osupa/decisions/',
    extraRestPaths: [],
  },
  OSUPA_SHORT: {
    id:             'OSUPA_SHORT',
    label:          'Osupa SHORT',
    sublabel:       'Overnight SHORT v3.1 PctFilt · NQ · 16:00 ET eval (close≤sma100 downtrend) · grade A/B take, C skip · Mon/Tue pct-above filter · −150bp stop · 09:30 ET exit · cert OSUPA-SHORT-PCTFILT-LOCK-4CC23AE0 · 63t · +36.7% · 71.4% WR · −2.34% DD · Phase 1',
    color:          '#a855f7',                 // purple-500 — distinct from OSUPA LONG indigo
    direction:      'SHORT',
    restPath:       '/strategies/api/strategies/osupa_short/decisions/',
    wsPath:         '/ws/strategies/osupa_short/decisions/',
    extraRestPaths: [],
  },
  ANYANWU: {
    id:             'ANYANWU',
    label:          'Anyanwu',
    sublabel:       'RTH grade-sized v3.0 · LONG every HOLD morning (A/B 1.0x, C 0.5x) · SHORT A/B 1.0x (C skip) · entry 09:30 → 16:00 ET · −125bp backstop · cert LONG 4FF3D5E0 / SHORT 11158C7C · L +60.9% / S +85.8% · Phase 1',
    color:          '#f59e0b',                 // amber-500 — "anyanwu" (Igbo: sun → RTH session)
    direction:      null,                      // A=LONG, C=SHORT
    restPath:       '/strategies/api/strategies/anyanwu/decisions/',
    wsPath:         '/ws/strategies/anyanwu/decisions/',
    extraRestPaths: [],
  },
  IYANU: {
    id:             'IYANU',
    label:          'Iyanu',
    sublabel:       'Overnight LONG (filtered) v2 - fires when OSUPA skips a genuine dip near SMA100 (pct_above<=400bp, ret5<-100bp) - 16:00->09:30 ET - -150bp stop - cert IYANU-LONG-LOCK-660E954F - 44t / 80% WR / +44.7bp - Phase 1',
    color:          '#22c55e',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/iyanu/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  JUYI: {
    id:             'JUYI',
    label:          'Juyi',
    sublabel:       'RTH 3m confluence reversal - LONG (moderate uptrend + pullback, 4+ algos) / SHORT (>800bp extended, 5+ algos) - ANYANWU-interaction sizing (separate book) - entry->16:00 ET - certs C6135B4B / 02191BEA - Phase 1',
    color:          '#a855f7',
    direction:      null,
    restPath:       '/strategies/api/strategies/juyi/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  ISALE: {
    id:             'ISALE',
    label:          'Isale',
    sublabel:       'Below-SMA overnight LONG (mean-reversion) - enter when RTH close < SMA100 - 16:00->09:30 ET - -175bp stop - mutually exclusive with OSUPA Long - cert ISALE-LONG-LOCK-1D2FEE32 - Phase 1',
    color:          '#ef4444',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/isale/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  SIRIUS: {
    id:             'SIRIUS',
    label:          'Sirius',
    sublabel:       'ES RTH momentum SHORT · Grade A 2× · ret1<-20bp · entry 09:30→16:00 ET · -150bp · cert ES-DESK-…8392E873 · SHADOW 1c',
    color:          '#a855f7',
    direction:      'SHORT',
    restPath:       '/strategies/api/strategies/sirius/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  RIGEL: {
    id:             'RIGEL',
    label:          'Rigel',
    sublabel:       'ES RTH trend LONG · Grade A>100bp/B · ret1(open[D-1] vs close[D-2])≥20bp & slope>0 · fires when SIRIUS skips · -200bp · SHADOW 1c',
    color:          '#2962ff',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/rigel/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  MARS: {
    id:             'MARS',
    label:          'Mars',
    sublabel:       'ES RTH · mirrors ANYANWU direction · Grade B(|pct_sma|>200bp)/D-skip · fires after SIRIUS+RIGEL skip · -75bp · SHADOW 1c',
    color:          '#e11d48',
    direction:      null,
    restPath:       '/strategies/api/strategies/mars/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  ISINMI: {
    id:             'ISINMI',
    label:          'Isinmi',
    sublabel:       'ES→NQ lull · enters NQ off the ES signal (SIRIUS→SHORT A / RIGEL→LONG B) when NQ slot free · runs after ANYANWU 09:30 · -200bp · SHADOW 1c',
    color:          '#f97316',
    direction:      null,
    restPath:       '/strategies/api/strategies/isinmi/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  SATURN: {
    id:             'SATURN',
    label:          'Saturn',
    sublabel:       'ES overnight LONG · NQ close<SMA100 → BUY ES · Grade B 1× · 16:00→09:30 ET · -100bp · SHADOW 1c',
    color:          '#0ea5e9',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/saturn/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  VENUS: {
    id:             'VENUS',
    label:          'Venus',
    sublabel:       'ES overnight LONG · pullback-in-uptrend (NQ above-SMA + slope>0 + 5d-ret<-100bp) · fires when SATURN skips · Grade B 1× · -150bp · SHADOW 1c',
    color:          '#0d9488',
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/venus/decisions/',
    wsPath:         null,
    extraRestPaths: [],
  },
  NUIT: {
    id:             'NUIT',
    label:          'Nuit',
    sublabel:       'Gold (GC1!) Overnight LONG · HOLD gate (close>SMA100 + rising) · 13:30→08:00 ET · −50bp stop (mandatory) · no grade · cert NUIT-GC-OVERNIGHT-LOCK-…16D8C109 · 626n / 61% WR / +169.4% · Phase 1',
    color:          '#eab308',                 // gold/amber — Gold (GC)
    direction:      'LONG',
    restPath:       '/strategies/api/strategies/nuit/decisions/',
    wsPath:         '/ws/strategies/nuit/decisions/',
    extraRestPaths: [],
  },
  TIER1_APREMIUM: {
    id:             'TIER1_APREMIUM',
    label:          'TIER 1',
    sublabel:       'Standalone A_PREMIUM · atr_trail · cert GBEMI-TIER1-CCH-20260609-954703A7 · Phase 1 LIVE · 3× SIZED · single-slot',
    color:          '#0ea5e9',                 // sky-500 — Tier 1 identity
    direction:      null,                       // long + short both supported
    restPath:       '/strategies/api/strategies/tier1_apremium/decisions/',
    wsPath:         null,                       // no WS yet; periodic poll
    extraRestPaths: [],
  },
  // TIER2_PRIME / TIER5_TRAIL removed from the app 2026-06-16.
  EXIT_ROUTER: {
    id:             'EXIT_ROUTER',
    label:          'Exit Router',
    sublabel:       'Opposite-signal exits · first-exit-wins · cross-strategy',
    color:          '#ef4444',                 // red-500 — exits
    direction:      null,
    restPath:       '/strategies/api/strategies/exit-router/decisions/',
    wsPath:         '/ws/strategies/exit-router/decisions/',
    extraRestPaths: [],
  },
  UNIFIED: {
    id:             'UNIFIED',
    label:          'Portfolio',
    sublabel:       '',
    color:          '#1e88e5',
    direction:      null,
    // Head feed = osupa. TIER1_APREMIUM removed from the homepage 2026-06-29 —
    // deactivated (backend TIER1_PAUSED=1 + beat disabled), so it must not lead the
    // Portfolio feed. (favola was head before that; removed in the Option A wipe.)
    restPath:       '/strategies/api/strategies/osupa/decisions/',
    wsPath:         null,
    extraRestPaths: [
      // GEMINI + NCHE + FAVOLA + TIER1 removed (retired/deactivated). osupa is the head feed above.
      // WURA retired 2026-06-29 (Director NQ Desk v2). Backend WURA_PAUSED=1.
      // 2026-06-29: Portfolio feed backfilled to ALL running books so the Decision Log +
      // Portfolio Feed reflect the live portfolio (not just the idle NQ/Gold core). The
      // IYANU/JUYI/ISALE decision endpoints were normalized to emit system_id + signal_ts.
      '/strategies/api/strategies/osupa_short/decisions/',
      '/strategies/api/strategies/anyanwu/decisions/',
      '/strategies/api/strategies/nuit/decisions/',
      '/strategies/api/strategies/iyanu/decisions/',
      '/strategies/api/strategies/juyi/decisions/',
      '/strategies/api/strategies/isale/decisions/',
      '/strategies/api/strategies/sirius/decisions/',
      '/strategies/api/strategies/rigel/decisions/',
      '/strategies/api/strategies/mars/decisions/',
      '/strategies/api/strategies/isinmi/decisions/',
      '/strategies/api/strategies/saturn/decisions/',
      '/strategies/api/strategies/venus/decisions/',
      // IYIPADA hidden 2026-06-22 (IYIPADA_PAUSED=1); KUNBI/EMEKA/imperial retired.
    ],
  },
};

// Allowlist of strategies actually shown to the user. The FEEDS map above
// stays full (helpers.js reads FEEDS.<id>.color for back-fed system_ids on
// records that pre-date this filter), but the navbar TabBar, the Portfolio
// aggregate, and the Backtest dropdown only surface entries that appear here.
// To add/remove a strategy from the UI, edit this list — no other changes
// needed.
export const VISIBLE_STRATEGIES = [
  // P1S1 and P1S1_LEVELS hidden from navbar 2026-06-08 by operator request.
  // Backend pause via P1S1_PAUSED=1 (env) + p1s1_levels_kill_switch.active=TRUE.
  // Re-add the IDs below to restore visibility once resumed.
  // IMPERIAL hidden 2026-06-19 — paused (imperial_kill_switch); re-add to restore.
  // 'IMPERIAL',        // Imperial NQ 30m ORB
  // EMEKA removed 2026-06-24 (strategy retired, operator request).
  // NCHE removed 2026-06-24; FAVOLA + GEMINI removed 2026-06-29 (Option A full wipe).
  // TIER1_APREMIUM hidden 2026-06-29 — deactivated pending go-ahead (backend TIER1_PAUSED=1 + tier1-walk-open beat disabled). Re-add to restore.
  // 'TIER1_APREMIUM',  // TIER 1 standalone A_PREMIUM strategy w/ atr_trail (cert GBEMI-TIER1-CCH-20260609-954703A7; Phase 1 LIVE 2026-06-10)
  // WURA retired 2026-06-29 (Director NQ Desk v2 — 33t, odd-hours burden not justified). Backend WURA_PAUSED=1.
  // 'WURA',            // WURA Deep-Europe NQ confluence (cert WURA-DEEPEU-LOCK-20260619-D7CCDDFF)
  // IYIPADA hidden 2026-06-22 by operator request. Backend paused via
  // IYIPADA_PAUSED=1 on prod. Re-add to restore on the navbar / portfolio.
  // 'IYIPADA',         // IYIPADA Cobra-15M Fade NQ (cert ONEWAY 7D3EEB85 / TAKE 20DBDA78; v2.0 feed-corrected, Phase 0 2026-06-21)
  'OSUPA',           // OSUPA Overnight LONG NQ v3.0 (cert OVERNIGHT-LONG-LOCK-309A9011; grade C-skip + scale-out 50/50; Phase 1 LIVE 2026-06-22)
  'OSUPA_SHORT',     // OSUPA Overnight SHORT NQ v3.0 (cert OSUPA-SHORT-LOCK-9F439770; downtrend SKIP nights, -150bp stop, grade C-skip; Phase 1 LIVE 2026-06-22)
  'ANYANWU',         // ANYANWU RTH session sibling of OSUPA (cert LONG 5EBBC8F8 / SHORT DF973BA8; A→LONG/C→SHORT, -125bp backstop, 16:00 flat; Phase 1 2026-06-24)
  'NUIT',            // NUIT Gold (GC1!) overnight LONG (cert NUIT-GC-OVERNIGHT-LOCK-…16D8C109; HOLD gate, -50bp stop, 13:30→08:00 ET; 626n/61%/+169.4%; Phase 1 2026-06-24)
  'IYANU',           // IYANU overnight LONG (filtered); OSUPA-skip dip; cert 660E954F; Phase 1 2026-06-27
  'JUYI',            // JUYI RTH confluence reversal (LONG+SHORT); separate book; Phase 1 2026-06-27
  'ISALE',           // ISALE below-SMA overnight LONG (mean-reversion); cert 1D2FEE32; Phase 1 2026-06-27
  // ── ES Desk (cert ES-DESK-…8392E873) — SHADOW / flat-1c, deployed 2026-06-29. ──
  'SIRIUS',          // ES RTH momentum SHORT (Grade A); Wave 1
  'RIGEL',           // ES RTH trend LONG (Grade A/B); Wave 2
  'MARS',            // ES RTH, mirrors ANYANWU dir (Grade B/D); Wave 3
  'ISINMI',          // ES→NQ lull (enters NQ off ES signal); Grade A/B
  'SATURN',          // ES overnight LONG (NQ<SMA); Grade B
  'VENUS',           // ES overnight LONG (pullback-in-uptrend); Grade B
  // TIER2_PRIME / TIER5_TRAIL removed from the app 2026-06-16 (history kept).
];

// Backtest reports the Director sees on the Backtest navbar page. Separate
// from VISIBLE_STRATEGIES because the backtest backend uses different keys
// (e.g. STRATA_PORTFOLIO_V16 vs the live STRATA feed id). The backend lists
// every report it can find; this list filters that down to the ones the
// Director wants surfaced.
export const VISIBLE_BACKTEST_STRATEGIES = [
  'STRATA_PORTFOLIO_V110',   // current cert A6E026BF (2026-06-02)
  'STRATA_PORTFOLIO_V16',    // prior cert 48901AD6 — kept for comparison/audit
  // GEMINI + FAVOLA removed 2026-06-29 (Option A full wipe).
  // 'KUNBI',                // discontinued 2026-06-19 (paused); re-add to restore on Backtest panel

  // NCHE_v4 / NCHE_v3 removed 2026-06-24 (strategy retired, operator request).
  // IYIPADA hidden 2026-06-22 by operator request — re-add to restore on the Backtest panel.
  // 'IYIPADA',                 // Cobra ONEWAY/TAKE 81-book v2.0 feed-corrected (cert 7D3EEB85 + 20DBDA78; +9.0%)
  'OSUPA',                   // overnight RSMA LONG 474-book (cert 4DACFF02; +51.8% / 60% WR / -4.4% maxDD)
  'WURA_v3',                 // pre-market MW+Lux+AA SKIP-coupled 105-book (cert 92858500; +15.2% / 58% WR; supersedes D7CCDDFF)
];

export const POLL_MS               = 5000;
export const WS_RECONNECT_INITIAL  = 1000;
export const WS_RECONNECT_MAX      = 30000;
export const WS_PING_INTERVAL      = 25000;

export const ALERT_SOUND_URL  = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/alert.mp3` : '/alert.mp3';
export const TOAST_DURATION   = 12000;
export const ALERT_MIN_GAP    = 1500;
export const LS_MUTE_KEY      = 'imole_alert_muted';
export const LS_THEME_KEY     = 'imole_theme';

// ─────────────────────────────────────────────────────────────
// Theme palettes — light (default) + dark, switched via Navbar's
// ThemeToggle. Selection is persisted in localStorage and applied
// at module load; toggling reloads the page so every component that
// imports `T` picks up the new palette without a context refactor.
// ─────────────────────────────────────────────────────────────

// Light theme — TradingView-inspired, tuned to "pop" on a white surface
const LIGHT_T = {
  bg:          '#f7f8fa',        // page background (soft off-white)
  surface:     '#ffffff',        // cards, panels
  surfaceHi:   '#eef1f6',        // selected / hover-high
  surfaceHov:  '#f5f7fa',        // hover
  border:      '#d9dde5',        // default border
  borderLight: '#e6e9ef',        // softer divider
  text:        '#131722',        // near-black, max contrast
  textDim:     '#4e5866',        // secondary text
  textMuted:   '#8892a1',        // muted labels
  bull:        '#089981',        // TradingView light-green
  bear:        '#f23645',        // TradingView light-red
  blue:        '#2962ff',        // strong action blue
  gold:        '#e09b00',        // stronger gold for contrast on white
  purple:      '#7c3aed',        // stronger purple
  bullBg:      'rgba(8,153,129,0.10)',
  bearBg:      'rgba(242,54,69,0.10)',
  blueBg:      'rgba(41,98,255,0.10)',
  purpleBg:    'rgba(124,58,237,0.10)',
  bullBorder:  'rgba(8,153,129,0.35)',
  bearBorder:  'rgba(242,54,69,0.35)',
  blueBorder:  'rgba(41,98,255,0.35)',
  purpleBorder:'rgba(124,58,237,0.35)',
};

// Dark theme — original TradingView-style palette (pre-light-mode default)
const DARK_T = {
  bg:          '#131722',
  surface:     '#1e2530',
  surfaceHi:   '#252d3d',
  surfaceHov:  '#1a2235',
  border:      'rgba(42,58,75,0.9)',
  borderLight: 'rgba(51,68,85,0.5)',
  text:        '#d1d4dc',
  textDim:     '#787b86',
  textMuted:   '#4f5966',
  bull:        '#26a69a',
  bear:        '#ef5350',
  blue:        '#1e88e5',
  gold:        '#f0b90b',
  purple:      '#9c6ef5',
  bullBg:      'rgba(38,166,154,0.08)',
  bearBg:      'rgba(239,83,80,0.08)',
  blueBg:      'rgba(30,136,229,0.1)',
  purpleBg:    'rgba(156,110,245,0.1)',
  bullBorder:  'rgba(38,166,154,0.3)',
  bearBorder:  'rgba(239,83,80,0.3)',
  blueBorder:  'rgba(30,136,229,0.3)',
  purpleBorder:'rgba(156,110,245,0.3)',
};

export const getTheme = () => {
  try { return localStorage.getItem(LS_THEME_KEY) === 'dark' ? 'dark' : 'light'; }
  catch { return 'light'; }
};

export const setTheme = (theme) => {
  try { localStorage.setItem(LS_THEME_KEY, theme === 'dark' ? 'dark' : 'light'); } catch {}
  // Reload so every component re-reads `T` from constants. Cheaper and safer
  // than threading a context through ~30 files for a one-time preference.
  if (typeof window !== 'undefined') window.location.reload();
};

// Apply the theme to <html data-theme="..."> at module load so the matching
// CSS rule in index.css (:root[data-theme="dark"]) overrides the --tv-* vars
// before any component paints. Without this, the JS-side T palette flips but
// the CSS-driven background/borders stay in light mode.
if (typeof document !== 'undefined') {
  document.documentElement.dataset.theme = getTheme();
}

export const T = getTheme() === 'dark' ? DARK_T : LIGHT_T;
